/**
 * SecurityGeoMap — Carte du monde avec markers d'alertes par pays.
 *
 * Utilise un SVG world map simplifié (chemins TopoJSON-light embarqués) +
 * projection equirectangular simple :
 *   x = (lon + 180) * width / 360
 *   y = (90 - lat) * height / 180
 *
 * Affiche :
 *  - Background SVG world map (équirectangulaire, frontières simplifiées)
 *  - Markers cercles dont la taille = log(count) + couleur = sévérité dominante
 *  - Tooltip au hover avec drapeau + count + top_kind
 *  - Tableau pays par pays en-dessous
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, Globe2, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { WORLD_POLYGONS, polygonToPath } from "./worldMapPaths";

const WIDTH = 1000;
const HEIGHT = 480;

const SEV_COLORS = {
  low: "#64748b",
  medium: "#f59e0b",
  high: "#f43f5e",
  critical: "#dc2626",
};

const projection = (lat, lon) => {
  const x = ((lon + 180) / 360) * WIDTH;
  const y = ((90 - lat) / 180) * HEIGHT;
  return [x, y];
};

const radius = (count) => Math.min(28, Math.max(5, Math.log2(count + 1) * 4 + 4));

export default function SecurityGeoMap({ onCountryClick }) {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(`/security/geo-stats?days=${days}`),
        api.get(`/security/alerts/timeline?days=${days}&granularity=${days <= 3 ? "hour" : "day"}`),
      ]);
      setStats(s.data);
      setTimeline(t.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [days]);

  return (
    <div className="space-y-4" data-testid="security-geo-map">
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h3 className="text-sm font-bold text-white inline-flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-violet-400" /> Carte mondiale des alertes ({stats?.total_alerts || 0} sur {days} jours)
          </h3>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="bg-[#14101c] border border-white/10 text-white text-xs rounded px-2 h-8" data-testid="geo-days-select">
              <option value={1}>24h</option>
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
            </select>
            <Button onClick={reload} variant="outline" size="sm" className="h-8 text-xs" data-testid="geo-refresh">
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
            </Button>
          </div>
        </div>

        {/* SVG Map */}
        <div className="relative bg-[#070512] rounded-lg overflow-hidden">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
            </div>
          ) : null}
          <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-auto" data-testid="geo-svg" style={{ background: "linear-gradient(180deg,#0a0717,#070512)" }}>
            {/* Grid lines (méridiens & parallèles tous les 30°) */}
            {[-60, -30, 0, 30, 60].map((lat) => (
              <line key={`p-${lat}`} x1={0} y1={projection(lat, 0)[1]} x2={WIDTH} y2={projection(lat, 0)[1]} stroke="rgba(255,255,255,0.03)" strokeWidth={0.5} />
            ))}
            {[-150, -120, -90, -60, -30, 0, 30, 60, 90, 120, 150].map((lon) => (
              <line key={`m-${lon}`} x1={projection(0, lon)[0]} y1={0} x2={projection(0, lon)[0]} y2={HEIGHT} stroke="rgba(255,255,255,0.03)" strokeWidth={0.5} />
            ))}
            {/* Continents (paths simplifiés) */}
            {WORLD_POLYGONS.map((poly, i) => (
              <path key={i} d={polygonToPath(poly, WIDTH, HEIGHT)} fill="rgba(139, 92, 246, 0.10)" stroke="rgba(139, 92, 246, 0.35)" strokeWidth={0.6} />
            ))}
            {/* Markers */}
            {stats?.countries?.map((c) => {
              if (c.lat == null || c.lon == null) return null;
              const [x, y] = projection(c.lat, c.lon);
              const r = radius(c.count);
              const color = SEV_COLORS[c.dominant_severity] || SEV_COLORS.medium;
              return (
                <g key={c.country_code} onClick={() => { setPinned(c); onCountryClick?.(c.country_code); }} onMouseEnter={() => setHovered(c)} onMouseLeave={() => setHovered(null)} style={{ cursor: "pointer" }} data-testid={`geo-marker-${c.country_code}`}>
                  <circle cx={x} cy={y} r={r + 6} fill={color} opacity={0.15}>
                    <animate attributeName="r" values={`${r + 4};${r + 10};${r + 4}`} dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.25;0.05;0.25" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={x} cy={y} r={r} fill={color} opacity={0.9} stroke={pinned?.country_code === c.country_code ? "white" : "rgba(255,255,255,0.6)"} strokeWidth={pinned?.country_code === c.country_code ? 2.5 : 1.2} />
                  <text x={x} y={y + 4} textAnchor="middle" fontSize="11" fontWeight="bold" fill="white" pointerEvents="none">{c.count}</text>
                </g>
              );
            })}
          </svg>

          {/* Tooltip */}
          {(hovered || pinned) && (
            <div className="absolute top-2 right-2 bg-[#14101c] border border-white/10 rounded-lg p-3 max-w-xs shadow-2xl" data-testid="geo-tooltip">
              {(() => {
                const c = hovered || pinned;
                return (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{c.flag_emoji}</span>
                      <div>
                        <div className="text-sm font-bold text-white">{c.country}</div>
                        <div className="text-[10px] text-slate-500">{c.country_code} · {c.unique_ips} IPs uniques</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-300">
                      <strong className="text-amber-300">{c.count}</strong> alertes · top : <span className="font-mono text-violet-300">{c.top_kind}</span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {Object.entries(c.by_severity).map(([k, v]) =>
                        v > 0 ? <span key={k} className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: SEV_COLORS[k] + "33", color: SEV_COLORS[k] }}>{k.toUpperCase()} {v}</span> : null
                      )}
                    </div>
                    {pinned && (
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                        <button onClick={() => setPinned(null)} className="text-[10px] text-slate-400 hover:text-white">Détacher</button>
                        {onCountryClick && <button onClick={() => onCountryClick(c.country_code)} className="text-[10px] text-violet-300 hover:text-violet-200 font-bold">Voir alertes →</button>}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Timeline */}
        {timeline?.series?.length > 0 && (
          <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3 mt-3" data-testid="timeline-chart">
            <div className="text-[10px] uppercase text-slate-500 font-bold mb-1">Timeline ({timeline.granularity})</div>
            <div className="flex items-end gap-0.5 h-12">
              {timeline.series.map((b, i) => {
                const max = Math.max(1, ...timeline.series.map((x) => x.total));
                const h = (b.total / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col justify-end" title={`${b.bucket} · ${b.total} alertes`}>
                    <div className="w-full" style={{
                      height: `${h}%`,
                      background: b.critical > 0 ? SEV_COLORS.critical : b.high > 0 ? SEV_COLORS.high : b.medium > 0 ? SEV_COLORS.medium : SEV_COLORS.low,
                      opacity: 0.85,
                    }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between text-[9px] text-slate-500 mt-1">
              <span>{timeline.series[0]?.bucket}</span>
              <span>{timeline.series[timeline.series.length - 1]?.bucket}</span>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-2 flex-wrap">
          <span>Sévérité dominante :</span>
          {Object.entries(SEV_COLORS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: v }} /> {k}
            </span>
          ))}
          <span className="ml-auto">Taille du cercle = nb d'alertes</span>
        </div>
      </div>

      {/* Top countries table */}
      {stats?.countries?.length > 0 && (
        <div className="bg-[#0b0811] rounded-xl border border-white/5" data-testid="geo-countries-table">
          <div className="p-3 border-b border-white/5 text-xs uppercase tracking-wider text-slate-400 font-bold">
            Pays sources des alertes ({stats.total_countries})
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[#14101c] text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left p-2.5">Pays</th>
                <th className="text-right p-2.5">Alertes</th>
                <th className="text-right p-2.5">IPs uniques</th>
                <th className="text-left p-2.5">Sévérité dominante</th>
                <th className="text-left p-2.5">Top kind</th>
              </tr>
            </thead>
            <tbody>
              {stats.countries.map((c) => (
                <tr key={c.country_code} className="border-t border-white/5 hover:bg-white/5" data-testid={`geo-row-${c.country_code}`}>
                  <td className="p-2.5">
                    <span className="text-lg mr-2">{c.flag_emoji}</span>
                    <span className="text-white font-bold">{c.country}</span>
                    <span className="text-slate-500 ml-1 text-[10px]">({c.country_code})</span>
                  </td>
                  <td className="p-2.5 text-right text-amber-300 font-bold">{c.count}</td>
                  <td className="p-2.5 text-right text-slate-300">{c.unique_ips}</td>
                  <td className="p-2.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase" style={{ background: SEV_COLORS[c.dominant_severity] + "33", color: SEV_COLORS[c.dominant_severity] }}>{c.dominant_severity}</span>
                  </td>
                  <td className="p-2.5 text-slate-300 font-mono text-[10px]">{c.top_kind}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
